import React, { useEffect } from 'react';
import {
	IconHome2,
	IconServer,
	IconBeer,
	IconBulb,
	IconMoon,
	IconBed,
	IconCoffee,
	IconFlame,
	IconBolt,
	IconDots,
} from '@tabler/icons-react';
import { useDashboard } from 'hooks';
import { useFlow } from 'hooks';
import getType from 'utils/deviceType';
import DashboardCardItem from './dashboardCardItem';

const ROOM_ICONS = {
	Vardagsrum: '🛋',
	Sovrum: '🛏',
	'Ellas rum': '⭐',
	'Elliots rum': '⭐',
	Kök: '☕',
	Kontor: '💻',
	Hall: '🚪',
	Tvättstuga: '🫧',
	Generellt: '🏠',
};

const clamp = (val, min, max) =>
	Math.min(100, Math.max(0, ((val - min) / (max - min)) * 100));

function StatCard({ label, Icon, value, barPercent, barColor, sub }) {
	return (
		<div
			className="rounded-[--radius-lg] p-4 flex flex-col gap-2"
			style={{ background: 'var(--color-bg-tertiary)' }}
		>
			<div className="flex justify-between items-start">
				<span
					className="text-[11px] font-medium"
					style={{ color: 'var(--color-text-secondary)' }}
				>
					{label}
				</span>
				<Icon size={16} style={{ color: 'var(--color-text-tertiary)' }} />
			</div>
			<div
				className="text-2xl font-semibold leading-none"
				style={{ color: 'var(--color-text-primary)' }}
			>
				{value}
			</div>
			{barPercent !== null && (
				<div
					className="h-[3px] rounded-full overflow-hidden"
					style={{ background: 'var(--color-bg-primary)' }}
				>
					<div
						className="h-full rounded-full transition-all"
						style={{ width: `${barPercent}%`, background: barColor }}
					/>
				</div>
			)}
			{sub && (
				<div
					className="text-[11px]"
					style={{ color: 'var(--color-text-tertiary)' }}
				>
					{sub}
				</div>
			)}
		</div>
	);
}

function getFlowIcon(name) {
	const n = name.toLowerCase();
	if (n.includes('hem')) return IconHome2;
	if (n.includes('natt')) return IconMoon;
	if (n.includes('läggning')) return IconBed;
	if (n.includes('lampor') || n.includes('lampa')) return IconBulb;
	if (n.includes('espresso') || n.includes('kaffe')) return IconCoffee;
	if (n.includes('mys')) return IconFlame;
	return IconBolt;
}

function DashboardCard() {
	const { items, itemClickEvent } = useDashboard();
	const { items: flows, handleGet } = useFlow();

	useEffect(() => {
		handleGet();
	}, []);

	const allItems = items ? items.flatMap((g) => g.items) : [];

	const generalGroup = items ? items.find((g) => g.name === 'Generellt') : null;
	const firstRoomGroup = items
		? items.find((g) => g.name !== 'Generellt')
		: null;

	const indoorTemp = firstRoomGroup
		? firstRoomGroup.items.find((i) => getType(i) === 'temperature')
		: null;
	const indoorHumidity = firstRoomGroup
		? firstRoomGroup.items.find((i) => getType(i) === 'humidity')
		: null;

	const serverTemp = allItems.find(
		(i) =>
			i.name.toLowerCase().includes('serverrack') &&
			getType(i) === 'temperature'
	);
	const serverHumidity = allItems.find(
		(i) =>
			i.name.toLowerCase().includes('serverrack') && getType(i) === 'humidity'
	);

	const olkylTemp = allItems.find(
		(i) => i.name.toLowerCase().includes('ölkyl') && getType(i) === 'temperature'
	);
	const olkylPower = allItems.find(
		(i) => i.name.toLowerCase().includes('ölkyl') && getType(i) === 'power'
	);
	const olkylEnergy = allItems.find(
		(i) => i.name.toLowerCase().includes('ölkyl') && getType(i) === 'consumption'
	);

	const statCards = [
		indoorTemp && {
			key: 'indoor',
			label: `Inomhus – ${firstRoomGroup ? firstRoomGroup.name.toLowerCase() : ''}`,
			Icon: IconHome2,
			value:
				indoorTemp.values && indoorTemp.values.description
					? indoorTemp.values.description
					: '—',
			barPercent: indoorTemp.values
				? clamp(parseFloat(indoorTemp.values.description), 15, 30)
				: null,
			barColor: '#76BC21',
			sub:
				indoorHumidity && indoorHumidity.values
					? `${indoorHumidity.values.description} luftfuktighet`
					: '',
		},
		serverTemp && {
			key: 'server',
			label: 'Serverrack',
			Icon: IconServer,
			value:
				serverTemp.values && serverTemp.values.description
					? serverTemp.values.description
					: '—',
			barPercent: serverTemp.values
				? clamp(parseFloat(serverTemp.values.description), 20, 50)
				: null,
			barColor: '#e8a020',
			sub:
				serverHumidity && serverHumidity.values
					? `${serverHumidity.values.description} luftfuktighet`
					: '',
		},
		olkylTemp && {
			key: 'olkyl',
			label: 'Ölkyl',
			Icon: IconBeer,
			value:
				olkylTemp.values && olkylTemp.values.description
					? olkylTemp.values.description
					: '—',
			barPercent: olkylTemp.values
				? clamp(parseFloat(olkylTemp.values.description), -5, 15)
				: null,
			barColor: '#4aaddc',
			sub: [
				olkylEnergy && olkylEnergy.values
					? `${olkylEnergy.values.description} totalt`
					: '',
				olkylPower && olkylPower.values
					? `${olkylPower.values.description} nu`
					: '',
			]
				.filter(Boolean)
				.join(' · '),
		},
	].filter(Boolean);

	const favoriteFlows =
		flows && flows.filter ? flows.filter((f) => f.favorite) : [];

	const roomGroups = items ? items.filter((g) => g.name !== 'Generellt') : [];
	const allGroups = items || [];

	return (
		<main className="max-w-7xl mx-auto px-6 py-5">
			{statCards.length > 0 && (
				<div
					className={`grid gap-3 mb-6 ${
						statCards.length === 3
							? 'grid-cols-3'
							: statCards.length === 2
							? 'grid-cols-2'
							: 'grid-cols-1'
					}`}
				>
					{statCards.map((card) => (
						<StatCard key={card.key} {...card} />
					))}
				</div>
			)}

			{(roomGroups.length > 0 || (generalGroup && allGroups.length > 0)) && (
				<>
					<p
						className="text-[11px] font-semibold uppercase tracking-widest mb-3"
						style={{ color: 'var(--color-text-tertiary)' }}
					>
						Rum
					</p>
					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5 mb-6">
						{allGroups.map((group) => (
							<div
								key={group.name}
								className="rounded-[--radius-lg] border overflow-hidden"
								style={{
									background: 'var(--color-bg-primary)',
									borderColor: 'var(--color-border-subtle)',
								}}
							>
								<div className="flex items-center justify-between px-4 py-3 border-b border-[--color-border-subtle]">
									<span
										className="text-[13px] font-semibold"
										style={{ color: 'var(--color-text-primary)' }}
									>
										{group.name}
									</span>
									<span className="text-base leading-none">
										{ROOM_ICONS[group.name] || '📦'}
									</span>
								</div>
								<div className="divide-y divide-[--color-border-subtle]">
									{group.items.map((item) => (
										<DashboardCardItem
											item={item}
											key={`${item.module}.${item.deviceId}`}
											onClick={() => itemClickEvent(item)}
										/>
									))}
								</div>
							</div>
						))}
					</div>
				</>
			)}

			{favoriteFlows.length > 0 && (
				<>
					<p
						className="text-[11px] font-semibold uppercase tracking-widest mb-3"
						style={{ color: 'var(--color-text-tertiary)' }}
					>
						Favorit-flows
					</p>
					<div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-2">
						{favoriteFlows.map((flow) => {
							const FlowIcon = getFlowIcon(flow.name);
							return (
								<button
									key={flow.id}
									type="button"
									className="flex items-center gap-2 px-3 py-3 rounded-[--radius-md] border text-left text-sm font-medium transition-colors hover:bg-[--color-bg-secondary]"
									style={{
										background: 'var(--color-bg-primary)',
										borderColor: '#76BC21',
										color: 'var(--color-text-primary)',
									}}
								>
									<FlowIcon
										size={15}
										className="shrink-0"
										style={{ color: '#76BC21' }}
									/>
									{flow.name}
								</button>
							);
						})}
						<button
							type="button"
							className="flex items-center gap-2 px-3 py-3 rounded-[--radius-md] border text-left text-sm font-medium transition-colors hover:bg-[--color-bg-secondary]"
							style={{
								background: 'var(--color-bg-primary)',
								borderColor: 'var(--color-border-subtle)',
								color: 'var(--color-text-secondary)',
							}}
						>
							<IconDots
								size={15}
								className="shrink-0"
								style={{ color: 'var(--color-text-tertiary)' }}
							/>
							Alla flows →
						</button>
					</div>
				</>
			)}
		</main>
	);
}

export default DashboardCard;
